import { useState, useEffect } from "react";
import api from "@/services/api";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Phone,
  CheckCircle2,
  FileText,
  Handshake,
  Trophy,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Euro,
  Activity,
  CalendarClock,
  Building2,
  FileSearch,
  Clock,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const stageConfig = {
  nuevo: { label: "Nuevos", icon: Users, color: "text-slate-400", bg: "bg-slate-400/10" },
  contactado: { label: "Contactados", icon: Phone, color: "text-blue-400", bg: "bg-blue-400/10" },
  calificado: { label: "Calificados", icon: CheckCircle2, color: "text-cyan-400", bg: "bg-cyan-400/10" },
  propuesta: { label: "Propuesta", icon: FileText, color: "text-purple-400", bg: "bg-purple-400/10" },
  negociacion: { label: "Negociación", icon: Handshake, color: "text-amber-400", bg: "bg-amber-400/10" },
  ganado: { label: "Ganados", icon: Trophy, color: "text-emerald-400", bg: "bg-emerald-400/10" },
  perdido: { label: "Perdidos", icon: XCircle, color: "text-red-400", bg: "bg-red-400/10" },
};

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [licStats, setLicStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [leadsRes, licRes] = await Promise.all([
        api.get('/leads/stats'),
        api.get('/licitaciones/stats').catch(() => null),
      ]);
      setStats(leadsRes.data);
      if (licRes) setLicStats(licRes.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Error cargando el dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyan-400 animate-pulse">Cargando dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-display theme-text mb-2">
          Hola, {user?.name?.split(" ")[0] || "Usuario"}
        </h1>
        <p className="theme-text-secondary">Resumen de tu pipeline de ventas</p>
      </div>

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Pipeline Value */}
        <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium theme-text-secondary flex items-center gap-2">
              <Euro className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
              Valor del Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono gradient-text" data-testid="pipeline-value">
              {formatCurrency(stats?.total_pipeline || 0)}
            </div>
            <p className="text-xs theme-text-muted mt-1">En oportunidades activas</p>
          </CardContent>
        </Card>

        {/* Total Leads */}
        <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium theme-text-secondary flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
              Total Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono theme-text" data-testid="total-leads">
              {stats?.total_leads || 0}
            </div>
            <p className="text-xs theme-text-muted mt-1">En el sistema</p>
          </CardContent>
        </Card>

        {/* Won Deals */}
        <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium theme-text-secondary flex items-center gap-2">
              <Trophy className="w-4 h-4 text-emerald-500" />
              Ganados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono text-emerald-500" data-testid="won-deals">
              {stats?.stages_count?.ganado || 0}
            </div>
            <p className="text-xs theme-text-muted mt-1">Deals cerrados</p>
          </CardContent>
        </Card>

        {/* Leads without activity */}
        <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium theme-text-secondary flex items-center gap-2">
              <AlertTriangle className={`w-4 h-4 ${stats?.leads_without_activity > 0 ? "text-amber-500" : ""}`} />
              Sin Actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats?.leads_without_activity > 0 ? "text-amber-500" : "theme-text"}`} data-testid="inactive-leads">
              {stats?.leads_without_activity || 0}
            </div>
            <p className="text-xs theme-text-muted mt-1">Más de 7 días</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline by Stage */}
      <div>
        <h2 className="text-xl font-semibold font-display theme-text mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />
          Pipeline por Etapa
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
          {Object.entries(stageConfig).map(([key, config]) => {
            const Icon = config.icon;
            const count = stats?.stages_count?.[key] || 0;
            
            return (
              <Card 
                key={key} 
                className="theme-bg-secondary hover:shadow-lg transition-all hover:-translate-y-1"
                style={{ border: '1px solid var(--theme-border)' }}
                data-testid={`stage-${key}`}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="text-2xl font-bold theme-text mb-1">{count}</div>
                  <p className="text-xs theme-text-muted">{config.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Licitaciones Widget */}
      {licStats && licStats.total > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold font-display theme-text flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-green-500" />
              Licitaciones Abiertas
            </h2>
            <button
              onClick={() => navigate("/licitaciones")}
              className="text-sm text-green-500 hover:text-green-400 flex items-center gap-1 transition-colors"
            >
              Ver todas <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-lg bg-green-400/10 flex items-center justify-center mx-auto mb-3">
                  <FileSearch className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-2xl font-bold theme-text mb-1">{licStats.total}</div>
                <p className="text-xs theme-text-muted">Total</p>
              </CardContent>
            </Card>
            <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-lg bg-emerald-400/10 flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="text-2xl font-bold text-emerald-400 mb-1">{licStats.por_categoria?.alta_prioridad || 0}</div>
                <p className="text-xs theme-text-muted">Alta Prioridad</p>
              </CardContent>
            </Card>
            <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-lg bg-amber-400/10 flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div className="text-2xl font-bold text-amber-400 mb-1">{licStats.por_categoria?.revisar || 0}</div>
                <p className="text-xs theme-text-muted">Revisar</p>
              </CardContent>
            </Card>
            <Card className="theme-bg-secondary hover:shadow-lg transition-all" style={{ border: '1px solid var(--theme-border)' }}>
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 rounded-lg bg-red-400/10 flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-5 h-5 text-red-400" />
                </div>
                <div className={`text-2xl font-bold mb-1 ${licStats.proximas_vencer_7d > 0 ? "text-red-400" : "theme-text"}`}>
                  {licStats.proximas_vencer_7d || 0}
                </div>
                <p className="text-xs theme-text-muted">Vencen 7d</p>
              </CardContent>
            </Card>
          </div>

          {/* Carga por Responsable */}
          {licStats.por_responsable && Object.keys(licStats.por_responsable).length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium theme-text-secondary mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-green-400" />
                Carga por Responsable
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(licStats.por_responsable)
                  .sort(([, a], [, b]) => b - a)
                  .map(([name, count], idx) => {
                    const colors = [
                      { text: "text-cyan-400", border: "border-cyan-500" },
                      { text: "text-purple-400", border: "border-purple-500" },
                      { text: "text-amber-400", border: "border-amber-500" },
                      { text: "text-rose-400", border: "border-rose-500" },
                      { text: "text-green-400", border: "border-green-500" },
                      { text: "text-indigo-400", border: "border-indigo-500" },
                      { text: "text-orange-400", border: "border-orange-500" },
                      { text: "text-pink-400", border: "border-pink-500" },
                    ];
                    const color = colors[idx % colors.length];
                    const parts = name.trim().split(" ");
                    const initials =
                      parts.length >= 2
                        ? (parts[0][0] + parts[1][0]).toUpperCase()
                        : name.substring(0, 2).toUpperCase();
                    return (
                      <div
                        key={name}
                        className="flex items-center gap-3 p-3 rounded-lg theme-bg-tertiary"
                        style={{ border: '1px solid var(--theme-border)' }}
                      >
                        <div
                          className={`w-9 h-9 rounded-full border-2 ${color.border} bg-slate-900/50 flex items-center justify-center ${color.text} text-sm font-bold flex-shrink-0`}
                        >
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium theme-text truncate">
                            {parts[0]}
                          </p>
                          <p className="text-xs theme-text-muted">{count} licitaciones</p>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Seguimientos del día */}
        <Card className="theme-bg-secondary" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-display theme-text flex items-center gap-2">
              <CalendarClock className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />
              Seguimientos de Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.seguimientos_hoy?.length > 0 ? (
              <div className="space-y-3">
                {stats.seguimientos_hoy.map((seg, idx) => (
                  <div
                    key={idx}
                    onClick={() => navigate("/leads")}
                    className="flex items-center gap-3 p-3 rounded-lg theme-bg-tertiary cursor-pointer hover:shadow-md transition-all"
                    style={{ border: '1px solid var(--theme-border)' }}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--theme-accent)', opacity: 0.15 }}>
                      <Building2 className="w-5 h-5" style={{ color: 'var(--theme-accent-dark)' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium theme-text truncate">{seg.empresa}</p>
                      <p className="text-sm theme-text-secondary truncate">{seg.contacto}</p>
                    </div>
                    <Badge className="stage-calificado">
                      {seg.tipo_seguimiento || "Llamada"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 theme-text-muted">
                <CalendarClock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Sin seguimientos programados para hoy</p>
                <p className="text-xs mt-1">Añade fechas de seguimiento a tus leads</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity Placeholder */}
        <Card className="theme-bg-secondary" style={{ border: '1px solid var(--theme-border)' }}>
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-display theme-text flex items-center gap-2">
              <Activity className="w-5 h-5" style={{ color: 'var(--theme-accent)' }} />
              Actividad Reciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 theme-text-muted">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Las actividades recientes aparecerán aquí</p>
              <p className="text-xs mt-1">Añade notas y actividades a tus leads</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
